import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { LoginService } from '../../../../Service/login.service';
import config from '../../../../config.json';
import { UsuarioService } from '../../../../Service/usuario.service';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private loginService: LoginService,
    private router: Router,
    private usuarioService: UsuarioService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const authMode = config.auth.AuthMode.toLowerCase() === 'true';
    const checkSupervisor =
      config.auth.CheckSupervisor.toLowerCase() === 'true';

    if (!authMode) {
      return of(true);
    }

    // ✅ PERMITIR ACCESO A RUTAS PÚBLICAS SIN VALIDACIÓN
    const publicRoutes = [
      '/auth/login',
      '/auth/adminLogin',
      '/home',
      '/home2',
      '/djnr',
      '/'
    ];

    // Limpiar query params y fragments para verificación
    const urlWithoutParams = state.url.split('?')[0].split('#')[0];

    const isPublicRoute = publicRoutes.some(route =>
      urlWithoutParams === route || urlWithoutParams.startsWith(route + '/')
    );

    if (isPublicRoute) {
      console.log('✅ Ruta pública permitida:', state.url);
      return of(true);
    }

    console.log('🔍 Ruta protegida detectada, validando sesión:', state.url);

    // ✅ Validar primero el contexto de la sesión
    return this.validateAndRefreshSession().pipe(
      switchMap((sessionValid) => {
        if (!sessionValid) {
          console.log('🚫 Session validation failed - redirigiendo a login');
          // Redirigir al login si la sesión no es válida
          this.router.navigate(['/auth/login']);
          return of(false);
        }

        // ✅ Si la sesión es válida, continuar con las validaciones existentes
        const token = this.getTokenFromCache();
        if (!token) {
          console.log('🚫 No token found after validation - redirigiendo a login');
          this.router.navigate(['/auth/login']);
          return of(false);
        }

        return this.performRoleBasedValidation(state, checkSupervisor);
      }),
      catchError((error) => {
        console.error('❌ Auth guard error - redirigiendo a login:', error);
        this.router.navigate(['/auth/login']);
        return of(false);
      })
    );
  }

  // ✅ Nuevo método para validar y refrescar la sesión
  private validateAndRefreshSession(): Observable<boolean> {
    return this.loginService.validateSessionContext().pipe(
      switchMap((isValid) => {
        if (!isValid) {
          return of(false);
        }

        // ✅ Intentar refrescar el token si es necesario
        return this.loginService.forceTokenRefresh().pipe(
          map((refreshed) => {
            if (refreshed) {
              console.log('✅ Session validated and token refreshed');
              return true;
            }
            console.log('⚠️ Token refresh failed');
            return false;
          }),
          catchError(() => {
            console.log('❌ Session validation error');
            return of(false);
          })
        );
      })
    );
  }

  // ✅ Separar la lógica de validación basada en roles
  private performRoleBasedValidation(
    state: RouterStateSnapshot,
    checkSupervisor: boolean
  ): Observable<boolean> {
    if (this.isAdmin()) {
      console.log('✅ Usuario es admin');
      return of(true);
    }

    if (checkSupervisor) {
      return this.validateSupervisorAccess(state);
    }

    return this.validateUserAccess(state);
  }

  // ✅ Validación para supervisores
  private validateSupervisorAccess(state: RouterStateSnapshot): Observable<boolean> {
    return this.isSupervisor().pipe(
      map((isSupervisor) => {
        if (isSupervisor) {
          return true;
        }

        const restrictedRoutesSafyc = [
          '/safyc/parametros/listadocodigos',
          '/safyc/parametros/cpparametros',
          '/safyc/resultados/analisis',
        ];
        const restrictedRoutesAdministracion = [
          '/administracion/users',
          '/administracion/useradd',
        ];

        if (restrictedRoutesSafyc.includes(state.url)) {
          this.router.navigate(['/safyc']);
          return false;
        } else if (restrictedRoutesAdministracion.includes(state.url)) {
          this.router.navigate(['/administracion/useredit']);
          return false;
        }
        return true;
      }),
      catchError((error) => {
        console.error('❌ Error al verificar si es supervisor:', error);
        this.loginService.logout();
        return of(false);
      })
    );
  }

  // ✅ Validación para usuarios regulares
  private validateUserAccess(state: RouterStateSnapshot): Observable<boolean> {
    const idUsuario = this.getIdUsuarioFromToken();

    if (!idUsuario) {
      console.error('❌ No se pudo obtener ID de usuario del token');
      this.loginService.logout();
      return of(false);
    }

    return this.usuarioService.getUsuarioById(Number(idUsuario)).pipe(
      map((usuario: any) => {
        if (usuario && usuario.rut) {
          const rutStr = usuario.rut
            .toString()
            .replace(/\./g, '')
            .replace('-', '');
          const rutNum = parseInt(rutStr.slice(0, -1), 10);

          const rutNumberList = [1001, 1002, 1003, 1004, 1005, 1006, 1007];

          if (rutNum < 10000 && !rutNumberList.includes(rutNum)) {
            const restrictedRoutesSafyc = [
              '/safyc/Sujetos/clientesregistrados',
            ];
            if (restrictedRoutesSafyc.includes(state.url)) {
              this.router.navigate(['/safyc']);
              return false;
            }
          }
        }
        return true;
      }),
      catchError((error) => {
        console.error('❌ Error al verificar RUT:', error);
        this.loginService.logout();
        return of(false);
      })
    );
  }

  isSupervisor(): Observable<boolean> {
    return this.usuarioService.isSupervisor().pipe(
      map((response: any) => {
        console.log('🔍 Is supervisor?', response);
        return response;
      }),
      catchError((error) => {
        console.error('❌ Error al verificar si es supervisor:', error);
        return of(false);
      })
    );
  }

  private getTokenFromCache(): string | null {
    if (typeof document !== 'undefined') {
      const token = this.loginService.getToken();
      return token;
    }
    return null;
  }

  private getUserRoleFromToken(): boolean | null {
    const token = this.getTokenFromCache();
    if (token) {
      try {
        const decodedToken: any = jwtDecode(token);
        return decodedToken.es_admin;
      } catch (error) {
        console.error('❌ Error al decodificar el token:', error);
        return null;
      }
    }
    console.log('⚠️ Token es null');
    return null;
  }

  isAdmin(): boolean {
    const es_admin = this.getUserRoleFromToken();
    const isAdminUser = es_admin === true;

    // ✅ Guardar el estado de admin en las cookies
    if (typeof document !== 'undefined') {
      if (isAdminUser) {
        this.setCookie('is_admin', 'true', 1); // Expira en 1 día
      } else {
        this.setCookie('is_admin', 'false', 1);
      }
    }

    return isAdminUser;
  }

  // ✅ Nuevo método para establecer cookies
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    const expiresString = expires.toUTCString();
    document.cookie = `${name}=${value};expires=${expiresString};path=/;secure;samesite=strict`;
  }

  // ✅ Nuevo método para obtener el estado de admin desde las cookies
  getIsAdminFromCookie(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    const name = 'is_admin=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i].trim();
      if (c.indexOf(name) === 0) {
        const value = c.substring(name.length, c.length);
        return value === 'true';
      }
    }
    return false;
  }

  // ✅ Método para limpiar la cookie de admin (útil en logout)
  clearAdminCookie(): void {
    if (typeof document !== 'undefined') {
      document.cookie = 'is_admin=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    }
  }

  getIsSupervisor(): Observable<boolean> {
    return this.isSupervisor();
  }

  private getIdUsuarioFromToken(): number | null {
    const token = this.getTokenFromCookies();

    if (!token) {
      console.error('❌ Token no disponible');
      return null;
    }

    try {
      const decodedToken: any = jwtDecode(token);
      return decodedToken.id || null;
    } catch (error) {
      console.error('❌ Error al decodificar el token:', error);
      return null;
    }
  }

  private getTokenFromCookies(): string | null {
    const name = 'token=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i].trim();
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return null;
  }
}
